/**
 * @file Audit log (`#/audit-log`) — admin only.
 *
 * Reads from `/audit_log` newest-first via a live listener. Rows are
 * clickable: clicking expands the row to reveal the full `details`
 * payload — particularly useful for the `system.sync_ran` row that
 * carries the per-source nightly diff.
 *
 * Design goals:
 *   • Machine notation, not prose — the Action column shows the raw
 *     dotted identifier (`onboarding.item_status`, `auth.signin`), and
 *     status codes render verbatim (`requested`, `handed_over`). The
 *     Summary column adds the useful values (SN, names, item labels) so
 *     the row is still scannable without becoming a feed.
 *   • The expanded view carries enough detail to RECREATE the original
 *     action's state (e.g. the SN entered at the prepared step). When
 *     a row needs to be replayed, the audit log is the source of truth.
 *   • Filtering is client-side over the latest N rows (default 500).
 *     Cheap (collection grows ~10-50 rows/day in normal operation) and
 *     dodges the need for composite indexes per filter combo.
 */

import { collection, query, orderBy, limit, onSnapshot, startAfter, getDocs } from 'firebase/firestore';

import { el, clear, formatDate } from '../utils.js';
import { Icons } from '../icons.js';
import { db }   from '../firebase.js';
import { AUDIT_ACTION } from '../lib/audit-actions.js';
import { renderPageHead } from '../page-header.js';
import { testCollectionName } from '../test-mode.js';
import { sortableTable } from '../lib/sortable-table.js';

const COLL = 'audit_log';
const PAGE_SIZE = 500;

// Audit log is machine notation by design — actions render verbatim
// (`auth.signin`, `onboarding.item_status`, …) and status transitions
// keep the raw stored codes. The summary column carries the human-useful
// values (SN, names, item labels) so the row is still scannable, but the
// log itself reads like a log, not a feed.
function transitionText(from, to) {
  return `${from || '-'} → ${to || '-'}`;
}

// ── Page ──────────────────────────────────────────────────────────────────
export async function renderAuditLog(host) {
  clear(host);

  host.appendChild(renderPageHead(
    'Audit log',
    'Every admin action plus nightly sync diffs. Click a row to see the full payload.',
  ));

  const state = { action: '', actor: '', target: '', dateFrom: '', dateTo: '' };

  const actionSelect = el('select', null,
    el('option', { value: '' }, 'All actions'),
    ...buildActionOptions(),
  );
  actionSelect.addEventListener('change', () => { state.action = actionSelect.value; rerender(); });

  const actorInput = el('input', { type: 'search', placeholder: 'Filter by actor email…' });
  actorInput.addEventListener('input', () => {
    state.actor = actorInput.value.trim().toLowerCase();
    rerender();
  });

  // "What happened to THIS device/person" — the question this page exists to
  // answer, previously unanswerable without expanding rows one by one.
  // Matches the target label/id plus the serial + names buried in details.
  const targetInput = el('input', { type: 'search', placeholder: 'Filter by device, serial or person…' });
  targetInput.addEventListener('input', () => {
    state.target = targetInput.value.trim().toLowerCase();
    rerender();
  });

  const fromInput = el('input', { type: 'date' });
  fromInput.addEventListener('change', () => { state.dateFrom = fromInput.value; rerender(); });

  const toInput = el('input', { type: 'date' });
  toInput.addEventListener('change', () => { state.dateTo = toInput.value; rerender(); });

  host.appendChild(el('div', { class: 'filters' },
    el('span', { class: 'filter-label' }, 'Filter'),
    actionSelect, actorInput, targetInput, fromInput, toInput,
  ));

  const tableHost = el('div', { class: 'card', style: 'padding:0' });
  host.appendChild(tableHost);
  const skeleton = el('div', { class: 'skeleton', style: 'height:300px' });
  tableHost.appendChild(skeleton);

  let rows = [];        // live newest page
  let olderRows = [];    // manually loaded history beyond the live window
  let oldestDoc = null;  // cursor: last doc of the live page (then of each older page)
  let noMoreOlder = false;
  const q = query(collection(db, testCollectionName(COLL)), orderBy('ts', 'desc'), limit(PAGE_SIZE));
  const unsub = onSnapshot(
    q,
    (snap) => {
      rows = snap.docs.map(toRow);
      if (!oldestDoc) oldestDoc = snap.docs[snap.docs.length - 1] || null;
      skeleton.remove();
      rerender();
    },
    (err) => {
      console.error('[audit-log] watch failed', err);
      skeleton.remove();
      clear(tableHost);
      tableHost.appendChild(el('div', { class: 'empty' },
        el('p', null, 'Could not load the audit log.'),
        el('p', { class: 'muted' }, err.message || String(err)),
      ));
    },
  );
  host.addEventListener('cleanup', unsub, { once: true });

  // "Load older" — the live query is capped at PAGE_SIZE, so anything past
  // that horizon (a disputed sale from last quarter) was simply invisible,
  // even with a date filter set. Pages backwards with a startAfter cursor.
  async function loadOlder(btn) {
    if (!oldestDoc || noMoreOlder) return;
    btn.disabled = true;
    const label = btn.textContent;
    btn.textContent = 'Loading…';
    try {
      const olderQ = query(
        collection(db, testCollectionName(COLL)),
        orderBy('ts', 'desc'), startAfter(oldestDoc), limit(PAGE_SIZE),
      );
      const snap = await getDocs(olderQ);
      olderRows = olderRows.concat(snap.docs.map(toRow));
      if (snap.docs.length) oldestDoc = snap.docs[snap.docs.length - 1];
      if (snap.docs.length < PAGE_SIZE) noMoreOlder = true;
      rerender();
    } catch (err) {
      console.error('[audit-log] load older failed', err);
      btn.disabled = false;
      btn.textContent = label;
    }
  }

  function rerender() {
    clear(tableHost);
    const all = rows.concat(olderRows);
    const filtered = applyFilters(all, state);
    const loadedNote = `${all.length} event${all.length === 1 ? '' : 's'} loaded`;
    const olderBtn = noMoreOlder
      ? null
      : el('button', { class: 'btn btn-sm' }, `Load ${PAGE_SIZE} older`);
    if (olderBtn) olderBtn.addEventListener('click', () => loadOlder(olderBtn));
    const footer = el('div', {
      class: 'row',
      style: 'justify-content:space-between;align-items:center;padding:12px 16px;border-top:1px solid var(--color-surface-pressed)',
    },
      el('span', { class: 'muted', style: 'font-size:var(--fs-small)' },
        noMoreOlder ? `${loadedNote} · reached the beginning` : loadedNote),
      olderBtn,
    );

    if (!filtered.length) {
      tableHost.appendChild(el('div', { class: 'empty' },
        Icons.search(),
        el('p', null, all.length ? 'No events match the current filters.' : 'No events recorded yet.'),
        all.length ? el('p', { class: 'muted' }, `${loadedNote}. Older events may exist — load more below.`) : null,
      ));
      tableHost.appendChild(footer);
      return;
    }
    tableHost.appendChild(sortableTable({
      columns: [
        { label: 'When',    sortValue: (r) => r.ts },
        { label: 'Actor',   sortValue: (r) => (r.actor && actorName(r.actor)) || r.actor?.email || r.actor?.serviceId },
        { label: 'Action',  sortValue: (r) => r.action },
        { label: 'Summary', sortable: false },
      ],
      rows: filtered,
      renderRow: eventRow,
      initialSort: { index: 0, dir: 'desc' }, // newest first (matches the query)
    }));
    tableHost.appendChild(footer);
  }
}

// ── Filtering ──────────────────────────────────────────────────────────────
function applyFilters(rows, state) {
  return rows.filter((r) => {
    if (state.action && r.action !== state.action) return false;
    if (state.actor) {
      const email = (r.actor?.email || '').toLowerCase();
      const name  = ((r.actor && actorName(r.actor)) || '').toLowerCase();
      if (!email.includes(state.actor) && !name.includes(state.actor)) return false;
    }
    if (state.target) {
      const d = r.details || {};
      const hay = [
        r.subject?.label, r.subject?.id,
        d.sn, d.employeeName, d.deviceId, d.name, d.itemName, d.employeeId,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(state.target)) return false;
    }
    const ts = tsToDate(r.ts);
    if (state.dateFrom) {
      const from = new Date(`${state.dateFrom}T00:00:00`);
      if (!ts || ts < from) return false;
    }
    if (state.dateTo) {
      const to = new Date(`${state.dateTo}T23:59:59.999`);
      if (!ts || ts > to) return false;
    }
    return true;
  });
}

function tsToDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts instanceof Date) return ts;
  return new Date(ts);
}

// ── Row rendering ─────────────────────────────────────────────────────────
function eventRow(r) {
  const date = tsToDate(r.ts);
  const dateText = date ? `${formatDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}` : '—';
  const actorLabel = formatActor(r.actor);
  const summary = oneLineSummary(r);

  const tr = el('tr', { style: 'cursor:pointer' },
    el('td', null, el('span', { class: 'muted' }, dateText)),
    el('td', null, actorLabel),
    el('td', null, el('code', { style: 'font-size:var(--fs-small)' }, r.action || '—')),
    el('td', null, summary),
  );
  let detailsRow = null;
  tr.addEventListener('click', () => {
    if (detailsRow) {
      detailsRow.remove();
      detailsRow = null;
      return;
    }
    detailsRow = el('tr', { class: 'audit-details' },
      el('td', { colspan: 4 }, renderDetails(r)),
    );
    tr.after(detailsRow);
  });
  return tr;
}

function pad(n) { return String(n).padStart(2, '0'); }

// Entries name what they were about in `subject` (the STRV standard);
// rows written before the hub move called it `target`. Some older server
// rows also carry only the actor's email — the name is filled in from any
// other row by the same person, so the Actor column reads the same.
const namesByEmail = new Map();
function toRow(d) {
  const r = { id: d.id, ...d.data() };
  if (!r.subject && r.target) r.subject = r.target;
  const email = r.actor?.email?.toLowerCase();
  if (email && r.actor.name) namesByEmail.set(email, r.actor.name);
  return r;
}
const actorName = (actor) => actor.name || namesByEmail.get(actor.email?.toLowerCase()) || null;

function formatActor(actor) {
  if (!actor) return '—';
  if (actor.type === 'system') {
    return el('span', { class: 'muted', title: actor.serviceId || '' }, actor.name || actor.serviceId || 'system');
  }
  return actorName(actor) || actor.email || actor.uid || '—';
}

// One-line summary per action. Plain text + the existing `.muted` grey
// for secondary info. No bold, no colour, no pills — those belong on
// state-bearing pages, not on a scrolling audit log.
function oneLineSummary(r) {
  const a = r.action;
  const d = r.details || {};
  const target = r.subject;

  switch (a) {
    case AUDIT_ACTION.AUTH_SIGNIN:
      return el('span', { class: 'muted' }, 'Signed in');

    case AUDIT_ACTION.ONBOARDING_STATUS: {
      const what = d.name || prettyCategory(d.category) || 'item';
      return el('span', null, `${what}: `,
        el('span', { class: 'muted' }, transitionText(d.from, d.to)),
        d.sn ? el('span', { class: 'muted' }, ` · SN ${d.sn}`) : null,
      );
    }

    case AUDIT_ACTION.ONBOARDING_FORM_MATCH:
      return el('span', null, `Linked form (${d.formFullName || d.privateEmail || '?'}) → `,
        el('span', { class: 'muted' }, target?.label || '?'),
      );

    case AUDIT_ACTION.ONBOARDING_FORM_IGNORE:
      return el('span', null, 'Dismissed form: ',
        el('span', { class: 'muted' }, d.fullName || d.privateEmail || target?.label || '?'),
      );

    case AUDIT_ACTION.OFFBOARDING_DEVICE:
      return el('span', null, `${target?.label || 'device'}: `,
        el('span', { class: 'muted' }, transitionText(d.from, d.to)),
        d.resaleValue != null ? el('span', { class: 'muted' }, ` · ${formatCZK(d.resaleValue)}`) : null,
      );

    case AUDIT_ACTION.OFFBOARDING_ASSET:
      return el('span', null, `${d.description || 'phone tariff'}: `,
        el('span', { class: 'muted' }, transitionText(d.from, d.to)),
      );

    case AUDIT_ACTION.OFFBOARDING_FINANCE_EXPORT:
      return el('span', null, 'Finance CSV exported',
        d.rowCount ? el('span', { class: 'muted' }, ` · ${d.rowCount} rows`) : null,
      );

    case AUDIT_ACTION.OFFBOARDING_DISMISS:
      return el('span', null, `${target?.label || d.employeeName || 'employee'} — dismissed`,
        d.cancelledCount ? el('span', { class: 'muted' }, ` · ${d.cancelledCount} device${d.cancelledCount === 1 ? '' : 's'} cancelled`) : null,
        d.note ? el('span', { class: 'muted' }, ` · "${d.note}"`) : null,
      );

    case AUDIT_ACTION.DEVICE_HANDOVER: {
      const what = target?.label || d.itemName || prettyCategory(d.category) || 'device';
      return el('span', null, what,
        d.direction ? el('span', { class: 'muted' }, ` · ${d.direction}`) : null,
        d.employeeName ? el('span', { class: 'muted' }, ` · ${d.employeeName}`) : null,
        d.sn ? el('span', { class: 'muted' }, ` · SN ${d.sn}`) : null,
      );
    }

    case AUDIT_ACTION.DEVICE_CREATE:
      return el('span', null, 'Added: ',
        el('span', { class: 'muted' }, target?.label || prettyCategory(d.category) || 'device'),
        d.sn ? el('span', { class: 'muted' }, ` · SN ${d.sn}`) : null,
      );

    case AUDIT_ACTION.DEVICE_PENDING_SUBMIT:
      return el('span', null,
        `${target?.label || 'device'} · `,
        el('span', { class: 'muted' }, d.action || '?'),
        d.toHolder ? el('span', { class: 'muted' }, ` → ${d.toHolder}`) : null,
        d.fromHolder ? el('span', { class: 'muted' }, ` ← ${d.fromHolder}`) : null,
        d.sale?.priceCzk != null ? el('span', { class: 'muted' }, ` · ${d.sale.priceCzk} CZK`) : null,
        d.slackOk === false ? el('span', { class: 'chip chip-error', style: 'margin-left:8px' }, 'Slack failed') : null,
      );

    case AUDIT_ACTION.DEVICE_PENDING_RECONCILED:
      return el('span', null,
        `${target?.label || 'device'} · `,
        el('span', { class: 'muted' }, d.via === 'abra_removed'
          ? `${d.action || '?'} resolved (device removed from Abra)`
          : `${d.action || '?'} reconciled in Abra`),
      );

    case AUDIT_ACTION.DEVICE_PENDING_CANCELLED:
      return el('span', null,
        `${target?.label || 'device'} · `,
        el('span', { class: 'muted' }, `${d.action || '?'} cancelled`),
        d.reason ? el('span', { class: 'muted' }, ` · "${d.reason}"`) : null,
      );

    case AUDIT_ACTION.DEVICE_STATUS_CHANGE:
      return el('span', null, `${target?.label || 'device'}: `,
        el('span', { class: 'muted' }, transitionText(d.from, d.to)),
      );

    case AUDIT_ACTION.ADMIN_ADD:
      return el('span', null, 'Added admin: ',
        el('span', { class: 'muted' }, d.email || target?.label || '?'),
      );

    case AUDIT_ACTION.ADMIN_REMOVE:
      return el('span', null, 'Removed admin: ',
        el('span', { class: 'muted' }, d.email || target?.label || '?'),
      );

    case AUDIT_ACTION.SYSTEM_SYNC_RAN: {
      const m = d.materialise || {};
      const flagged = (m.holderMismatch || 0) + (m.orphanNoAbra || 0);
      const parts = [];
      if (d.bamboo)  parts.push(`${d.bamboo.totalSeen ?? '?'} people`);
      if (d.flexi)   parts.push(`${d.flexi.totalSeen ?? '?'} Abra`);
      if (d.meraki)  parts.push(`${d.meraki.totalSeen ?? '?'} Meraki`);
      if (d.iru)     parts.push(`${d.iru.totalSeen ?? '?'} IRU`);
      if (d.forms && d.forms.matched != null) parts.push(`${d.forms.matched} form ${d.forms.matched === 1 ? 'match' : 'matches'}`);
      if (flagged)   parts.push(`${flagged} flagged`);
      const lead = d.source === 'manual' ? 'Manual sync' : 'Nightly sync';
      return el('span', null, `${lead}: `,
        el('span', { class: 'muted' }, parts.join(' · ') || 'no data'),
      );
    }

    case AUDIT_ACTION.SYSTEM_ASSETS_SYNC:
      return el('span', null, 'Phone tariffs refreshed: ',
        el('span', { class: 'muted' }, `${d.updated ?? 0} scanned · ${d.withTariff ?? 0} active`),
      );

    case AUDIT_ACTION.SYSTEM_CONFIG_CHANGE:
      return el('span', null, 'Config: ',
        el('span', { class: 'muted' }, target?.label || d.path || '?'),
      );

    default:
      return el('span', { class: 'muted' }, target?.label || '—');
  }
}

// ── Expanded detail view ──────────────────────────────────────────────────
//
// Per action: structured layout. For SYSTEM_SYNC_RAN we keep the rich
// section-per-source view from before. Other actions get a definition list
// so the values are scannable and copy-pasteable without JSON brackets.
function renderDetails(r) {
  const wrap = el('div', { class: 'audit-details-inner' });

  if (r.action === AUDIT_ACTION.SYSTEM_SYNC_RAN) {
    wrap.appendChild(renderSyncDetails(r.details || {}));
    return wrap;
  }
  if (r.action === AUDIT_ACTION.SYSTEM_CONFIG_CHANGE) {
    wrap.appendChild(renderConfigChangeDetails(r));
    return wrap;
  }
  wrap.appendChild(renderStructuredDetails(r));
  return wrap;
}

// Per-action structured view — single kv table, no badges, no pills.
// Every meaningful field is shown by name so the audit log doubles as a
// restoration ledger. Empty values render as em-dashes for alignment.
function renderStructuredDetails(r) {
  const d = r.details || {};
  const rows = [];

  // Actor + target always come first so the row's "who/where" is at the
  // top of the expansion.
  rows.push(['Actor', formatActorFull(r.actor)]);
  if (r.subject) {
    rows.push(['Target', el('span', null,
      r.subject.label || '—',
      el('span', { class: 'muted', style: 'margin-left:6px;font-family:var(--font-serial);font-size:var(--fs-small)' },
        `${r.subject.type}:${r.subject.id}`,
      ),
    )]);
  }

  for (const row of detailRowsForAction(r.action, d)) rows.push(row);

  return el('table', { class: 'kv' }, ...rows.map(([k, v]) =>
    el('tr', null,
      el('td', { class: 'k' }, k),
      el('td', { class: 'v' }, v == null || v === '' ? el('span', { class: 'muted' }, '—') : v),
    ),
  ));
}

// Per-action field list for the expansion. Each entry is [label, value].
// Plain values: strings, numbers, code-tagged ids/SNs, formatted CZK.
// No pills, no colour. Falls through to a JSON dump if no formatter is
// defined for the action (so we never silently lose data).
function detailRowsForAction(action, d) {
  switch (action) {
    case AUDIT_ACTION.AUTH_SIGNIN:
      return [];

    case AUDIT_ACTION.ONBOARDING_STATUS:
      return [
        ['Item',        d.name || prettyCategory(d.category) || null],
        ['Category',    d.category ? el('code', null, d.category) : null],
        ['Item id',     d.itemId ? el('code', null, d.itemId) : null],
        ['From',        d.from ? el('code', null, d.from) : null],
        ['To',          d.to   ? el('code', null, d.to)   : null],
        ['SN',          d.sn ? el('code', null, d.sn) : null],
        ['Subcategory', d.subcategory || null],
        ['Notes',       d.notes || null],
      ];

    case AUDIT_ACTION.ONBOARDING_FORM_MATCH:
      return [
        ['Submission id',   d.submissionId ? el('code', null, d.submissionId) : null],
        ['Form name',       d.formFullName || null],
        ['Private email',   d.privateEmail || null],
        ['Items imported',  d.itemCount != null ? String(d.itemCount) : null],
        ['Categories',      (d.categories || []).join(', ') || null],
      ];

    case AUDIT_ACTION.ONBOARDING_FORM_IGNORE:
      return [
        ['Submission id', d.submissionId ? el('code', null, d.submissionId) : null],
        ['Form name',     d.fullName || null],
        ['Private email', d.privateEmail || null],
        ['Items skipped', d.itemCount != null ? String(d.itemCount) : null],
      ];

    case AUDIT_ACTION.OFFBOARDING_DEVICE:
      return [
        ['Employee',     d.employeeName || d.employeeId || null],
        ['From',         d.from ? el('code', null, d.from) : null],
        ['To',           d.to   ? el('code', null, d.to)   : null],
        ['Resale value', d.resaleValue != null ? formatCZK(d.resaleValue) : null],
      ];

    case AUDIT_ACTION.OFFBOARDING_ASSET:
      return [
        ['Asset',           d.description || null],
        ['Bamboo asset id', d.bambooAssetId ? el('code', null, d.bambooAssetId) : null],
        ['From',            d.from ? el('code', null, d.from) : null],
        ['To',              d.to   ? el('code', null, d.to)   : null],
      ];

    case AUDIT_ACTION.OFFBOARDING_FINANCE_EXPORT:
      return [
        ['Rows exported', d.rowCount != null ? String(d.rowCount) : null],
        ['Employees',     (d.employeeIds || []).join(', ') || null],
      ];

    case AUDIT_ACTION.OFFBOARDING_DISMISS: {
      const cancelled = Array.isArray(d.cancelledDevices) ? d.cancelledDevices : [];
      return [
        ['Employee',         d.employeeName || null],
        ['End date',         d.endDate || null],
        ['Previous phase',   d.previousPhase ? el('code', null, d.previousPhase) : null],
        ['Cancelled count',  d.cancelledCount != null ? String(d.cancelledCount) : null],
        ['Cancelled devices', cancelled.length
          ? el('ul', { style: 'margin:0;padding-left:18px' },
              ...cancelled.map((c) => el('li', null,
                c.name || prettyCategory(c.category) || c.deviceId,
                c.sn ? el('span', { class: 'muted' }, ` · SN ${c.sn}`) : null,
                c.previousStatus ? el('span', { class: 'muted' }, ` · was `, el('code', null, c.previousStatus)) : null,
              )),
            )
          : null,
        ],
        ['Note',             d.note || null],
      ];
    }

    case AUDIT_ACTION.DEVICE_HANDOVER:
      return [
        ['Direction',    d.direction ? el('code', null, d.direction) : null],
        ['Category',     d.category ? el('code', null, d.category) : null],
        ['Item / model', d.itemName || null],
        ['SN',           d.sn ? el('code', null, d.sn) : null],
        ['Employee',     d.employeeName || d.employeeId || null],
        ['From holder',  d.fromHolder ? el('code', null, d.fromHolder) : null],
        ['Outcome',      d.outcome ? el('code', null, d.outcome) : null],
        ['Resale value', d.resaleValue != null ? formatCZK(d.resaleValue) : null],
        ['Bamboo asset id', d.bambooAssetId ? el('code', null, d.bambooAssetId) : null],
      ];

    case AUDIT_ACTION.DEVICE_CREATE:
      return [
        ['Category', d.category ? el('code', null, d.category) : null],
        ['SN',       d.sn ? el('code', null, d.sn) : null],
      ];

    case AUDIT_ACTION.DEVICE_PENDING_SUBMIT:
      return [
        ['Action',         d.action ? el('code', null, d.action) : null],
        ['Pending id',     d.pendingId ? el('code', null, d.pendingId) : null],
        ['SN',             d.sn ? el('code', null, d.sn) : null],
        ['Category',       d.category ? el('code', null, d.category) : null],
        ['From holder',    d.fromHolder || null],
        ['To holder',      d.toHolder || null],
        ['Sale buyer',     d.sale?.buyer || null],
        ['Sale price',     d.sale?.priceCzk != null ? `${d.sale.priceCzk} CZK` : null],
        ['Sale suggested', d.sale?.suggested != null ? `${d.sale.suggested} CZK` : null],
        ['Sale external',  d.sale?.isExternal === true ? 'yes' : (d.sale ? 'no' : null)],
        ['Note',           d.note || null],
        ['Slack',          d.slackOk === true ? 'sent' : (d.slackError || (d.slackOk === false ? 'failed' : null))],
      ];

    case AUDIT_ACTION.DEVICE_PENDING_RECONCILED:
      return [
        ['Action',     d.action ? el('code', null, d.action) : null],
        ['Pending id', d.pendingId ? el('code', null, d.pendingId) : null],
        ['SN',         d.sn ? el('code', null, d.sn) : null],
        ['Resolved via', d.via ? el('code', null, d.via) : null],
        ['Note',       d.note || null],
        ['Age',        d.ageMs != null ? `${Math.round(d.ageMs / 1000 / 60)} min` : null],
      ];

    case AUDIT_ACTION.DEVICE_PENDING_CANCELLED:
      return [
        ['Action',     d.action ? el('code', null, d.action) : null],
        ['Pending id', d.pendingId ? el('code', null, d.pendingId) : null],
        ['SN',         d.sn ? el('code', null, d.sn) : null],
        ['Reason',     d.reason || null],
        ['Slack',      d.slackOk === true ? 'sent' : (d.slackError || (d.slackOk === false ? 'failed' : null))],
      ];

    case AUDIT_ACTION.DEVICE_STATUS_CHANGE:
      return [
        ['From', d.from ? el('code', null, d.from) : null],
        ['To',   d.to   ? el('code', null, d.to)   : null],
      ];

    case AUDIT_ACTION.ADMIN_ADD:
      return [
        ['Email',        d.email || null],
        ['Display name', d.displayName || null],
      ];

    case AUDIT_ACTION.ADMIN_REMOVE:
      return [
        ['Email',      d.email || null],
        ['Removed by', d.removedBy || null],
      ];

    case AUDIT_ACTION.SYSTEM_ASSETS_SYNC:
      return [
        ['Fetched',                    d.totalFetched != null ? String(d.totalFetched) : null],
        ['Skipped (done, no tariff)',  d.skippedDoneNoTariff != null ? String(d.skippedDoneNoTariff) : null],
        ['Updated',                    d.updated != null ? String(d.updated) : null],
        ['With active tariff',         d.withTariff != null ? String(d.withTariff) : null],
        ['Errors',                     d.errors != null ? String(d.errors) : null],
        ['Duration',                   d.durationMs != null ? `${(d.durationMs / 1000).toFixed(1)}s` : null],
      ];

    default:
      return [];
  }
}

function formatActorFull(actor) {
  if (!actor) return '—';
  if (actor.type === 'system') {
    return el('span', null,
      el('strong', null, actor.name || actor.serviceId || 'system'),
      actor.serviceId ? el('span', { class: 'muted', style: 'margin-left:6px' }, `· ${actor.serviceId}`) : null,
    );
  }
  return el('span', null,
    el('strong', null, actorName(actor) || actor.email || actor.uid || '—'),
    actor.email && actorName(actor) ? el('span', { class: 'muted', style: 'margin-left:6px' }, `· ${actor.email}`) : null,
    actor.via === 'hub' ? el('span', { class: 'muted', style: 'margin-left:6px' }, '· via IT Hub') : null,
  );
}

// ── system.config_change: before/after diff ───────────────────────────────
// Compact key=before → after lines for each changed field. Unchanged
// fields omitted. Matches the audit-log house style: no badges, no
// per-cell colouring, dense.
function renderConfigChangeDetails(r) {
  const d = r.details || {};
  const before = d.before || {};
  const after  = d.after  || {};
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));

  const rows = [
    ['Path', el('code', null, d.path || '?')],
  ];
  for (const k of keys) {
    const bv = formatConfigValue(before[k]);
    const av = formatConfigValue(after[k]);
    const changed = JSON.stringify(before[k]) !== JSON.stringify(after[k]);
    if (!changed) continue;
    rows.push([k, el('span', null, bv, el('span', { class: 'muted' }, ' → '), av)]);
  }
  if (rows.length === 1) {
    rows.push(['(no field-level changes)', el('span', { class: 'muted' }, 'before == after')]);
  }
  return el('table', { class: 'kv' }, ...rows.map(([k, v]) =>
    el('tr', null, el('td', { class: 'k' }, k), el('td', { class: 'v' }, v)),
  ));
}

function formatConfigValue(v) {
  if (v == null) return el('span', { class: 'muted' }, '—');
  if (Array.isArray(v)) return v.length ? `${v.length} item${v.length === 1 ? '' : 's'}` : el('span', { class: 'muted' }, 'empty');
  if (typeof v === 'object') return el('code', { style: 'font-size:var(--fs-small)' }, JSON.stringify(v));
  return String(v);
}

// ── system.sync_ran: per-source sub-sections (kept from previous shape) ──
function renderSyncDetails(d) {
  const wrap = el('div', { class: 'stack' });

  if (d.bamboo) {
    wrap.appendChild(renderSyncSection('BambooHR (people)', {
      'Total seen':   d.bamboo.totalSeen,
      'Added':        d.bamboo.added,
      'Updated':      d.bamboo.updated,
      'Errors':       d.bamboo.errors,
    }, [
      d.bamboo.newJoiners?.length     ? listSection('New joiners',   d.bamboo.newJoiners.map((j)   => `${j.name || j.bambooId} → ${j.phase || '-'}`)) : null,
      d.bamboo.phaseChanges?.length   ? listSection('Phase changes', d.bamboo.phaseChanges.map((c) => `${c.name || c.bambooId}: ${c.from || '-'} → ${c.to || '-'}`)) : null,
      d.bamboo.stickyHeldOpen?.length ? listSection('Held in offboarding (gear unresolved)',
        d.bamboo.stickyHeldOpen.map((h) => `${h.name || h.bambooId} · ${h.pendingItems ?? h.pendingDevices} pending`)) : null,
      d.bamboo.rehires?.length ? listSection('Re-hires surfaced into onboarding',
        d.bamboo.rehires.map((r) => `${r.name || r.bambooId} → starts ${r.start || '?'}`)) : null,
    ].filter(Boolean)));
  }
  if (d.flexi) {
    wrap.appendChild(renderSyncSection('Abra (devices)', {
      'Total seen':   d.flexi.totalSeen,
      'Added':        d.flexi.added,
      'Updated':      d.flexi.updated,
      'Retired':      d.flexi.retiredCount,
      'Errors':       d.flexi.errors,
    }, [
      d.flexi.newDevices?.length    ? listSection('New devices',    d.flexi.newDevices.map((x)    => `${x.flexiKod} · ${x.name || x.sn || '?'}`)) : null,
      d.flexi.holderChanges?.length ? listSection('Holder changes', d.flexi.holderChanges.map((c) => `${c.name || c.flexiKod}: ${c.fromEmail || '(none)'} → ${c.toEmail || '(none)'}`)) : null,
    ].filter(Boolean)));
  }
  if (d.meraki) {
    wrap.appendChild(renderSyncSection('Meraki MDM', {
      'Total seen':   d.meraki.totalSeen,
      'Added':        d.meraki.added,
      'Updated':      d.meraki.updated,
      'Retired':      d.meraki.retiredCount,
      'Errors':       d.meraki.errors,
    }, [
      d.meraki.newDevices?.length   ? listSection('New devices',   d.meraki.newDevices.map((x)   => `${x.sn || x.merakiId} · ${x.model || ''} → ${x.ownerEmail || '(no owner)'}`)) : null,
      d.meraki.ownerChanges?.length ? listSection('Owner changes', d.meraki.ownerChanges.map((c) => `${c.name || c.merakiId}: ${c.fromEmail || '(none)'} → ${c.toEmail || '(none)'}`)) : null,
    ].filter(Boolean)));
  }
  if (d.iru) {
    wrap.appendChild(renderSyncSection('IRU MDM', {
      'Total seen':   d.iru.totalSeen,
      'Added':        d.iru.added,
      'Updated':      d.iru.updated,
      'Retired':      d.iru.retiredCount,
      'Errors':       d.iru.errors,
    }, [
      d.iru.newDevices?.length   ? listSection('New devices',   d.iru.newDevices.map((x)   => `${x.sn || x.iruId} · ${x.model || ''} → ${x.ownerEmail || '(no owner)'}`)) : null,
      d.iru.ownerChanges?.length ? listSection('Owner changes', d.iru.ownerChanges.map((c) => `${c.name || c.iruId}: ${c.fromEmail || '(none)'} → ${c.toEmail || '(none)'}`)) : null,
    ].filter(Boolean)));
  }
  if (d.testing) {
    wrap.appendChild(renderSyncSection('Testing devices (sheet)', {
      'Total rows':   d.testing.totalRows,
      'Added':        d.testing.added,
      'Updated':      d.testing.updated,
      'Swept':        d.testing.swept,
      'Skipped':      d.testing.skipped,
      'Errors':       d.testing.errors,
    }, [
      d.testing.newDevices?.length ? listSection('New devices',
        d.testing.newDevices.map((x) => `${x.deviceTag || '?'} · ${x.model || '?'} · ${x.sheetTab || '?'}`)) : null,
    ].filter(Boolean)));
  }
  if (d.materialise) {
    wrap.appendChild(renderSyncSection('Materialise (join)', {
      'Matched':              d.materialise.matched,
      'Holder mismatch':      d.materialise.holderMismatch,
      'Abra unassigned':      d.materialise.abraUnassigned,
      'Orphan (no Abra)':     d.materialise.orphanNoAbra,
      'Decommissioned':       d.materialise.decommissioned,
      'Decom. still in MDM':  d.materialise.decomStillInMdm,
      'Abra-only (no MDM)':   d.materialise.abraOnlyNoMeraki,
      'Testing matched':      d.materialise.testingMatched,
      'Testing wrong holder': d.materialise.testingWrongHolder,
      'Testing not in Abra':  d.materialise.testingNotInAbra,
      'Swept Flexi':          d.materialise.sweptFlexi,
      'Swept Meraki':         d.materialise.sweptMeraki,
      'Swept Testing':        d.materialise.sweptTesting,
      'Gear adopted':         d.materialise.gearAdopted,
    }));
  }
  if (d.forms) {
    wrap.appendChild(renderSyncSection('Joining-form sheet', {
      'Total rows':   d.forms.totalRows,
      'Processed':    d.forms.processed,
      'Matched':      d.forms.matched,
      'Unmatched':    d.forms.unmatched,
      'Skipped':      d.forms.skipped,
      'Errors':       d.forms.errors,
    }));
  }
  if (Array.isArray(d.errors) && d.errors.length) {
    wrap.appendChild(el('div', { class: 'section-head' }, 'Errors'));
    wrap.appendChild(el('ul', null, ...d.errors.map((e) =>
      el('li', null, `${e.stage}: ${e.error}`),
    )));
  }
  if (typeof d.durationMs === 'number') {
    wrap.appendChild(el('p', { class: 'muted', style: 'margin-top:8px' },
      `Duration: ${(d.durationMs / 1000).toFixed(1)}s`));
  }
  return wrap;
}

function renderSyncSection(title, counts, extras = []) {
  return el('section', { class: 'card', style: 'padding:14px' },
    el('div', { class: 'section-head' }, title),
    el('div', { class: 'row', style: 'gap:18px;flex-wrap:wrap' },
      ...Object.entries(counts).map(([k, v]) => el('span', null,
        el('span', { class: 'label', style: 'margin-right:4px' }, `${k}: `),
        el('span', null, v != null ? String(v) : '—'),
      )),
    ),
    ...extras,
  );
}

function listSection(title, items) {
  const max = 50;
  const truncated = items.length > max;
  const list = el('ul', { style: 'margin:8px 0 0;padding-left:18px' },
    ...items.slice(0, max).map((line) => el('li', null, line)),
  );
  if (truncated) list.appendChild(el('li', { class: 'muted' }, `… ${items.length - max} more`));
  return el('div', { style: 'margin-top:10px' },
    el('div', { class: 'label' }, `${title} (${items.length})`),
    list,
  );
}

// ── Misc ──────────────────────────────────────────────────────────────────
function prettyCategory(c) {
  if (!c) return null;
  return c.replace(/_/g, ' ');
}

function formatCZK(value) {
  if (value == null || Number.isNaN(Number(value))) return el('span', { class: 'muted' }, '—');
  return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }).format(Number(value));
}

// Filter dropdown: group actions by domain, render the action string
// verbatim — the same notation the rows show, so the filter and the row
// label match exactly.
function buildActionOptions() {
  const grouped = {};
  for (const action of Object.values(AUDIT_ACTION)) {
    const [domain] = action.split('.', 1);
    (grouped[domain] ||= []).push(action);
  }
  const optgroups = [];
  for (const domain of Object.keys(grouped).sort()) {
    optgroups.push(el('optgroup', { label: domain },
      ...grouped[domain].sort().map((a) => el('option', { value: a }, a)),
    ));
  }
  return optgroups;
}
